import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useReducer, useRef, useState } from 'react';
import { isFirefox, isWindows } from 'react-device-detect';
import { useNavigate } from 'react-router';
import styled, { css } from 'styled-components';
import { useLocationHref } from '../../hooks/location';
import { useWakeLock } from '../../hooks/useWakeLock';
import { useHasSupportedVideoCodec, useShare } from '../../utils';
import { useTimedFlag } from '../../utils/time';
import { Anchor } from '../Anchor';
import { Button } from '../Button';
import { Canvas } from '../Canvas';
import { FullscreenButton } from '../FullscreenButton';
import { ShareIcon, SpeakerIcon } from '../Icon';
import { LiveIndicator } from '../LiveIndicator';
import { LoadingIndicator } from '../LoadingIndicator';
import { CodecNotInstalled, CodecNotSupported, FirefoxNotSupported, } from '../NotSupportedMessage';
import { Stage } from '../Stage';
import SvgIcon from '../SvgIcon';
import { ViewerPreLive } from '../ViewerPreLive';
import { openViewerOptions } from '../popups/ViewerOptionsPopup';
import { BottomViewToolbar, StageContainer, StatusContainer, TopViewToolbar, ViewRoot, ViewToolbarSegment, } from './View';
export function ViewerView({ streamId }) {
    const [ready, setReady] = useReducer(() => true, false);
    const [isVideoStreaming, setIsVideoStreaming] = useTimedFlag(false, 5000);
    const [isAudioOnMute, setIsAudioOnMute] = useState();
    const navigate = useNavigate();
    const { canShare, openSystemShareDialog } = useShare();
    const browserVideoCodecSupported = useHasSupportedVideoCodec();
    const canvasControllerRef = useRef(null);
    const stageContainerRef = useRef(null);
    const [isHudVisible, showHud] = useTimedFlag(true, 5000);
    useWakeLock({ enabled: true });
    const url = useLocationHref();
    // biome-ignore lint/correctness/useExhaustiveDependencies: Track the time the user started watching *this* stream
    const watchingStartedAt = useMemo(() => {
        return new Date();
    }, [streamId]);
    const optionsButtonAnchorRef = useRef(null);
    if (isFirefox) {
        return _jsx(FirefoxNotSupported, {});
    }
    if (browserVideoCodecSupported === false) {
        if (isWindows) {
            return _jsx(CodecNotInstalled, {});
        }
        return _jsx(CodecNotSupported, {});
    }
    return (_jsxs(_Fragment, { children: [_jsx(LoadingIndicator, { loading: !ready }), _jsxs(ViewRoot, { onTouchStart: () => {
                    showHud();
                }, onMouseDown: () => {
                    showHud();
                }, onMouseOver: () => {
                    showHud();
                }, children: [_jsx(TopViewToolbar, { children: _jsx(StatusContainer, { children: isVideoStreaming && (_jsx(LiveIndicator, { streamId: streamId, startedAt: watchingStartedAt })) }) }), _jsx(StageContainer, { ref: stageContainerRef, children: _jsxs(Stage, { children: [_jsx(Canvas, { controllerRef: canvasControllerRef, streamId: streamId, onMuteChange: setIsAudioOnMute, onReady: () => setReady(), onReceivedFrame: () => setIsVideoStreaming(), fit: "contain", overlay: _jsx(Hud, { "$visible": isHudVisible, children: _jsx(FullscreenButton, { element: stageContainerRef.current }) }) }), !isVideoStreaming && _jsx(ViewerPreLive, {})] }) }), _jsx(BottomViewToolbar, { children: _jsxs(Buttons, { "$canShare": canShare, children: [_jsx("div", {}), _jsx(Button, { type: "button", shape: "pill", padding: "none", kind: isAudioOnMute ? undefined : 'secondary', variant: isAudioOnMute ? undefined : 'dark', icon: _jsx(SpeakerIcon, { muted: isAudioOnMute }), onClick: () => {
                                        if (isAudioOnMute) {
                                            canvasControllerRef.current?.unmute();
                                        }
                                        else {
                                            canvasControllerRef.current?.mute();
                                        }
                                    } }), canShare && (_jsx(Button, { type: "button", kind: "secondary", shape: "pill", padding: "none", variant: "dark", icon: _jsx(ShareIcon, {}), onClick: () => {
                                        openSystemShareDialog(url);
                                    } })), _jsx(Anchor, { ref: optionsButtonAnchorRef, children: _jsx(Button, { type: "button", kind: "secondary", shape: "pill", padding: "none", variant: "dark", icon: _jsx(SvgIcon, { name: "dotDotDot" }), onClick: () => {
                                            openViewerOptions({
                                                anchorRef: optionsButtonAnchorRef,
                                            });
                                        } }) }), _jsx(Button, { type: "button", kind: 'destructive', shape: "pill", onClick: () => {
                                        navigate('/');
                                    }, children: "Leave" }), _jsx("div", {})] }) })] })] }));
}
const Buttons = styled(ViewToolbarSegment) `
    grid-template-columns: 1fr auto auto auto auto 1fr;

    ${({ $canShare }) => !$canShare &&
    css `
            grid-template-columns: 1fr auto auto auto 1fr;
        `}
`;
const Hud = styled.div `
    position: absolute;
    top: 1rem;
    right: 1rem;
    z-index: 10;
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    transition: opacity 0.3s ease-in-out;
    pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
`;
